import type * as React from 'react'

import {
  ArrowLeftIcon,
  Button,
  Dialog,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from '@buffer-mono/popcorn'

import styles from '../index.module.css'

import type { JSX } from 'react'

type FooterAction = {
  children: React.ReactNode
  disabled?: boolean
  loading?: boolean
  onClick: () => void | Promise<void>
  tooltip?: string
  variant: 'primary' | 'critical' | 'tertiary'
}

type CancelFlowStepFrameProps = {
  children?: React.ReactNode
  isLoading?: boolean
  onBack?: () => void
  primaryAction?: FooterAction
  secondaryAction?: FooterAction
  title?: React.ReactNode
  titleProps?: Omit<React.ComponentProps<typeof Dialog.Title>, 'children'>
  description?: React.ReactNode
  descriptionProps?: Omit<
    React.ComponentProps<typeof Dialog.Description>,
    'children'
  >
  introClassName?: string
  descriptionWrapperClassName?: string
  contentClassName?: string
  contentGap?: React.ComponentProps<typeof Flex>['gap']
  stepLabel?: string
}

export const CancelFlowStepFrame = ({
  children,
  isLoading = false,
  onBack,
  primaryAction,
  secondaryAction,
  title,
  titleProps,
  description,
  descriptionProps,
  introClassName,
  descriptionWrapperClassName,
  contentClassName,
  contentGap,
  stepLabel,
}: CancelFlowStepFrameProps): JSX.Element => (
  <>
    <Dialog.Header className={styles.stepHeader}>
      {onBack ? (
        <IconButton
          onClick={onBack}
          label="Back"
          variant="tertiary"
          size="small"
          disabled={isLoading}
          className={styles.neutralIconButton}
        >
          <ArrowLeftIcon />
        </IconButton>
      ) : (
        <span />
      )}
      {stepLabel ? (
        <Text size="sm" color="subtle" className={styles.stepIndicator}>
          {stepLabel}
        </Text>
      ) : (
        <span />
      )}
    </Dialog.Header>
    <Dialog.Body className={styles.stepBody}>
      <Flex
        direction="column"
        align="stretch"
        fullWidth
        gap={contentGap}
        className={contentClassName}
      >
        {(title || description) && (
          <div className={introClassName}>
            {title && (
              <Dialog.Title
                size="lg"
                align="center"
                className={styles.stepHeading}
                {...titleProps}
              >
                {title}
              </Dialog.Title>
            )}
            {description && descriptionWrapperClassName && (
              <div className={descriptionWrapperClassName}>
                <Dialog.Description {...descriptionProps}>
                  {description}
                </Dialog.Description>
              </div>
            )}
            {description && !descriptionWrapperClassName && (
              <Dialog.Description {...descriptionProps}>
                {description}
              </Dialog.Description>
            )}
          </div>
        )}
        {children}
      </Flex>
    </Dialog.Body>
    {(secondaryAction || primaryAction) && (
      <Dialog.Footer className={styles.footerActions}>
        {secondaryAction && (
          <Button
            variant={secondaryAction.variant}
            size="large"
            disabled={secondaryAction.disabled || isLoading}
            loading={secondaryAction.loading}
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.children}
          </Button>
        )}
        {primaryAction &&
          (primaryAction.tooltip ? (
            <Tooltip
              content={primaryAction.tooltip}
              open={primaryAction.disabled ? undefined : false}
            >
              <span className={styles.disabledTooltipTrigger}>
                <Button
                  variant={primaryAction.variant}
                  size="large"
                  disabled={primaryAction.disabled || isLoading}
                  loading={primaryAction.loading}
                  onClick={primaryAction.onClick}
                  className={
                    primaryAction.disabled
                      ? styles.disabledTooltipButton
                      : undefined
                  }
                >
                  {primaryAction.children}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant={primaryAction.variant}
              size="large"
              disabled={primaryAction.disabled || isLoading}
              loading={primaryAction.loading}
              onClick={primaryAction.onClick}
            >
              {primaryAction.children}
            </Button>
          ))}
      </Dialog.Footer>
    )}
    <Dialog.CloseButton disabled={isLoading} />
  </>
)
